import { Suspense, Component, lazy } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage } from 'react-intl';

// project imports
import Loader from './Loader';
import { isChunkLoadError } from 'utils/chunkErrorUtils';
import tempNoInternetImage from '../../src/assets/images/temp-no-internet.png';

// ==============================|| RETRY DYNAMIC IMPORT ||============================== //

const CHUNK_RELOAD_KEY = 'chunk_reload';
const NETWORK_CONNECTIVITY_ERRORS = ['ERR_CONNECTION_TIMED_OUT', 'ERR_ADDRESS_UNREACHABLE'];

function isNetworkConnectivityError(error) {
  if (!error) return false;
  const errorText = `${error?.name || ''} ${error?.message || ''}`.toUpperCase();
  return NETWORK_CONNECTIVITY_ERRORS.some((code) => errorText.includes(code));
}

/**
 * Wraps a dynamic import() call with retry logic.
 * On failure, retries up to `maxRetries` times with a delay.
 * If all retries fail, forces a full page reload (once) to fetch fresh assets.
 */
function lazyRetry(importFn, maxRetries = 2, retryDelay = 1000) {
  return lazy(
    () =>
      new Promise((resolve, reject) => {
        const attempt = (retriesLeft) => {
          importFn()
            .then((module) => {
              // Import succeeded — clear the reload flag so future chunk errors can auto-reload again
              sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              resolve(module);
            })
            .catch((error) => {
              if (retriesLeft > 0) {
                setTimeout(() => attempt(retriesLeft - 1), retryDelay);
              } else {
                // All retries exhausted — try a hard reload once to get fresh asset manifest
                if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                  sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
                  window.location.reload();
                } else {
                  // Already reloaded once, let the error boundary handle it
                  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                  reject(error);
                }
              }
            });
        };

        attempt(maxRetries);
      })
  );
}

// ==============================|| CHUNK ERROR BOUNDARY ||============================== //

/**
 * Class-based error boundary that catches chunk/module load failures
 * and presents a user-friendly retry UI instead of crashing the app.
 */
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = isChunkLoadError(this.state.error);
      const isBrowserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const isConnectivityError = isNetworkConnectivityError(this.state.error);
      const shouldShowOfflineState = isChunkError || isBrowserOffline || isConnectivityError;
      const title = shouldShowOfflineState ? (
        <FormattedMessage id="networkConnectivityErrorTitle" />
      ) : (
        <FormattedMessage id="loadableGenericErrorTitle" />
      );
      const subtitle = shouldShowOfflineState ? null : <FormattedMessage id="loadableGenericErrorDescription" />;
      const buttonLabel = shouldShowOfflineState ? 'Retry' : 'Reload Page';

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            gap: 2,
            p: 3
          }}
        >
          {shouldShowOfflineState && (
            <Box
              component="img"
              src={tempNoInternetImage}
              alt="No internet connection"
              sx={{
                width: { xs: 160, sm: 220 },
                maxWidth: '100%',
                objectFit: 'contain'
              }}
            />
          )}
          <Typography variant="h5" color="text.primary" textAlign="center">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {subtitle}
            </Typography>
          )}
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleReload} sx={{ mt: 1 }}>
            {buttonLabel}
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// ==============================|| LOADABLE - LAZY LOADING ||============================== //

export default function Loadable(importFn) {
  // Use lazyRetry instead of passing an already-created lazy component
  const LazyComponent = typeof importFn === 'function' && !importFn._payload ? lazyRetry(importFn) : importFn;

  const WrappedComponent = (props) => (
    <ChunkErrorBoundary>
      <Suspense fallback={<Loader />}>
        <LazyComponent {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  );

  return WrappedComponent;
}

export { lazyRetry, ChunkErrorBoundary };
